// Angular modules
import { Component }            from '@angular/core';
import { ViewChild }            from '@angular/core';

// Ionic modules
import { GoogleAnalytics }      from '@ionic-native/google-analytics';
import { LoadingController }    from 'ionic-angular';
import { Nav }                  from 'ionic-angular';
import { Platform }             from 'ionic-angular';
import { ToastController }      from 'ionic-angular';

// External modules
import { CartPage }             from './pages/cart/cart';
import { ContactPage }          from './pages/contact/contact';
import { GascomsuptionPage }    from './pages/gascomsuption/gascomsuption';
import { GasselectorPhonePage } from './pages/gasselector-phone/gasselector-phone';
import { GasselectorPage }      from './pages/gasselector/gasselector';
import { HomeMenuPage }         from './pages/home-menu/home-menu';
import { HomePage }             from './pages/home/home';
import { InformationPage }      from './pages/information/information';
import { KeyPointsPage }        from './pages/key-points/key-points';
import { SettingsPage }         from './pages/settings/settings';
import { SynchronizePage }      from './pages/synchronize/synchronize';
import { ApplicationService }   from './shared/providers/application-service';
import { AuthService }          from './shared/providers/auth-service';
import { DocumentService }      from './shared/providers/document-service';
import { TranslateService }     from '@ngx-translate/core';

// Internal modules
import { environment }          from '../environments/environment';

// Helpers
import { StorageHelper }        from './shared/helpers/storage.helper';

// Services
import { BuildInfoService }     from './shared/providers/build-info.service';

@Component({
  templateUrl: 'app.html'
})
export class MyApp {
  @ViewChild(Nav) nav: Nav;

  rootPage    : any = HomePage;
  pages       : Array<{title: string, component: any, icon: string, visible: boolean, notif: boolean}>;
  showNotif   : boolean = false;
  isCheatMode : boolean  = false;

  constructor(
    private translate         : TranslateService,
    private platform          : Platform,
    private applicationService: ApplicationService,
    private documentService   : DocumentService,
    private authService       : AuthService,
    private buildInfoService  : BuildInfoService,
    private toastCtrl         : ToastController,
    private analytics         : GoogleAnalytics,
    private loadingCtrl       : LoadingController)
  {
    this.getSelectedLang(()=>
    {
      this.initData();
    });

    // Get build information
    this.buildInfoService.getBuildInfo().subscribe(info => {
      if(info === null) {
        console.error('Build info is null');
        return;
      }

      console.info('=====================================');
      console.info(`Application Version: ${environment.VERSION}`);
      console.info(`Build ID: ${info.version}`);
      console.info(`Build Date: ${info.date.toISOString()}`);
      console.info('=====================================');
    });

    platform.ready().then(async () =>
    {
      this.analytics.startTrackerWithId("UA-90550294-1");

      const loader = this.presentLoading();
      loader.present();
      this.presentToast('Checking your session validity', 1000);

      const validity = await this.authService.checkAuthValidity();
      switch(validity)
      {
        case 'no user':
          this.nav.push(HomePage,{}, {animate: false});
          break;

        case 'invalid':
          StorageHelper.setUser(null);
          this.presentToast('Your session has expired, please login again');
          this.nav.push(HomePage,{}, {animate: false});
          break;

        case 'offline':
          this.presentToast(`Application is in offline mode`);

          if(StorageHelper.getDocument() == null)
            this.nav.push(SynchronizePage,{}, {animate: false});

        case 'valid':
          const user = StorageHelper.getUser();
          this.presentToast('Welcome to MAP App ' + user.email);
          this.nav.push(HomeMenuPage,{}, {animate: false});
          break;
      }
      loader.dismiss();

      this.platform.registerBackButtonAction(() =>
      {
        if(this.nav.canGoBack())
        {
          this.nav.pop({animate:false});
          return;
        }

        this.nav.setRoot(this.nav.getPrevious());
      });
    });
  }

  private presentLoading()
  {
    let loader = this.loadingCtrl.create({
      content: 'Loading...'
    });

     return loader;
  }

  public openPage(page) : void
  {
    this.nav.push(page.component,{}, {animate: false});
  }

  public async menuOpened() : Promise<void>
  {
    this.isCheatMode = StorageHelper.getCheat();

    const hasUpdate = await this.documentService.hasDocumentUpdate();
    this.showNotif = hasUpdate;
    this.initData();
  }

  public doGoogleLogout() : void
  {
    StorageHelper.removeUser();
    StorageHelper.removeCheat();
    this.nav.setRoot(HomePage);
  }

  private initData() : void
  {
    let tablet = this.platform.is('tablet') || this.platform.is('core');

    this.pages =
    [
      {
        title     : 'HOME',
        component : HomeMenuPage,
        icon      :'assets/home_blue_icn.png',
        visible   : true,
        notif     : false
      },
      {
        title     :'GASSELECTOR',
        component : tablet ? GasselectorPage : GasselectorPhonePage,
        icon      :'assets/gasselector_blue_icn.png',
        visible   : true,
        notif     : false
      },
      {
        title     : 'CONSUMPTION_CALCULATOR',
        component : GascomsuptionPage,
        icon      :'assets/consuption_blue_icn.png',
        visible   : true,
        notif     : false
      },
      {
        title     : 'KEY_POINTS_ABOUT_MAP',
        component : KeyPointsPage,
        icon      :'assets/mixtures_blue_icn.png',
        visible   : true,
        notif     : false
      },
      {
        title     : 'CONTACT',
        component : ContactPage,
        icon      :'assets/contact_blue_icn.png',
        visible   : true,
        notif     : false
      },
      {
        title     : 'CART',
        component : CartPage,
        icon      : 'assets/cart_blue_icn.png' ,
        visible   : true,
        notif     : false
      },
      {
        title     : 'SYNCHRONIZE',
        component : SynchronizePage,
        icon      :'assets/synchronize_blue_icn.png',
        visible   : true,
        notif     : this.showNotif
      },
      {
        title     : 'SETTINGS',
        component : SettingsPage,
        icon      :'assets/settings_blue_icn.png',
        visible   : true,
        notif     : false
      },
      {
        title     : 'INFORMATION',
        component : InformationPage,
        icon      : 'assets/information_blue_icn.png' ,
        visible   : true,
        notif     : false
      }
    ];
  }

  private presentToast(message, duration = 3000) : void
  {
    let toast = this.toastCtrl.create(
    {
      message: message,
      duration
    });
    toast.present();
  }

  private async getSelectedLang(callback) : Promise<any>
  {

    const lang = await this.applicationService.getSelectedLanguage()
    this.translate.use(lang);
    callback();
  }
}




// WEBPACK FOOTER //
// ./src/app/app.component.ts